import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { UserCog, CheckCircle2, PauseCircle, Users, Building2 } from "lucide-react";

const STATUS = {
  active: { bg: "bg-emerald-50", text: "text-emerald-700", label: "Active" },
  pending: { bg: "bg-amber-50", text: "text-amber-700", label: "Pending" },
  inactive: { bg: "bg-slate-100", text: "text-slate-600", label: "Inactive" },
};

export default function SocialWorkerAdmin({ counties, onChanged }) {
  const [workers, setWorkers] = useState([]);
  const [parents, setParents] = useState([]);
  const [countyPick, setCountyPick] = useState({});
  const [assignFor, setAssignFor] = useState(null); // parent being assigned
  const [assignWorkerId, setAssignWorkerId] = useState("");

  const load = async () => {
    try {
      const [w, p] = await Promise.all([api.get("/social-workers"), api.get("/billing/parents")]);
      setWorkers(w.data);
      setParents(p.data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  useEffect(() => {
    load();
  }, []);

  const setAccess = async (worker, access_status) => {
    const county_id = countyPick[worker.id] ?? worker.county_id ?? "";
    try {
      await api.put(`/social-workers/${worker.id}/access`, { access_status, county_id: county_id || null });
      toast.success(access_status === "active" ? "Social worker activated" : "Access updated");
      await load();
      onChanged?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const assignParent = async () => {
    try {
      await api.put(`/parents/${assignFor.id}/assign-worker`, { worker_id: assignWorkerId || null });
      toast.success(assignWorkerId ? "Client assigned" : "Client unassigned");
      setAssignFor(null);
      setAssignWorkerId("");
      await load();
      onChanged?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const activeWorkers = workers.filter((w) => w.access_status === "active");

  return (
    <div className="space-y-8">
      {/* Social workers */}
      <div>
        <h3 className="flex items-center gap-2 font-display text-lg font-semibold text-slate-900">
          <UserCog className="h-5 w-5 text-[#1e3a8a]" /> Social Workers
        </h3>
        <p className="text-sm text-slate-500">Assign to a county, then activate to consume a seat. Deactivating frees a seat without deleting data.</p>
        <div className="mt-4 space-y-3" data-testid="social-workers-list">
          {workers.map((w) => {
            const s = STATUS[w.access_status] || STATUS.pending;
            return (
              <div key={w.id} data-testid={`worker-row-${w.id}`} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-slate-800">{w.name}</span>
                      <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${s.bg} ${s.text}`}>{s.label}</span>
                    </div>
                    <p className="mt-0.5 text-xs text-slate-500">
                      {w.email} · {w.county_name || "No county"} · {w.assigned_client_count} client(s)
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <select
                      data-testid={`worker-county-${w.id}`}
                      value={countyPick[w.id] ?? w.county_id ?? ""}
                      onChange={(e) => setCountyPick({ ...countyPick, [w.id]: e.target.value })}
                      className="h-9 rounded-md border border-slate-200 bg-white px-2 text-sm"
                    >
                      <option value="">Select county…</option>
                      {counties.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name} ({c.seats?.seats_available ?? 0} free)
                        </option>
                      ))}
                    </select>
                    {w.access_status === "active" ? (
                      <Button size="sm" variant="outline" data-testid={`deactivate-worker-${w.id}`} onClick={() => setAccess(w, "inactive")} className="border-red-200 text-red-600 hover:bg-red-50">
                        <PauseCircle className="mr-1.5 h-4 w-4" /> Deactivate
                      </Button>
                    ) : (
                      <Button size="sm" data-testid={`activate-worker-${w.id}`} onClick={() => setAccess(w, "active")} className="bg-emerald-600 hover:bg-emerald-700">
                        <CheckCircle2 className="mr-1.5 h-4 w-4" /> Activate
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
          {workers.length === 0 && <p className="py-6 text-center text-sm text-slate-400">No social workers registered yet.</p>}
        </div>
      </div>

      {/* Client assignment */}
      <div>
        <h3 className="flex items-center gap-2 font-display text-lg font-semibold text-slate-900">
          <Users className="h-5 w-5 text-[#0d9488]" /> Client Assignment
        </h3>
        <p className="text-sm text-slate-500">Assign each parent/client to an active social worker.</p>
        <div className="mt-4 space-y-2" data-testid="client-assignment-list">
          {parents.map((p) => (
            <div key={p.id} data-testid={`assign-row-${p.id}`} className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-slate-200 bg-white p-3">
              <div>
                <p className="font-medium text-slate-800">{p.name}</p>
                <p className="text-xs text-slate-500">
                  {p.county_name || "No county"} ·{" "}
                  {p.assigned_worker_name ? <span className="text-[#0d9488]">Worker: {p.assigned_worker_name}</span> : "Unassigned"}
                </p>
              </div>
              <Button size="sm" variant="outline" data-testid={`assign-client-btn-${p.id}`} onClick={() => { setAssignFor(p); setAssignWorkerId(p.assigned_worker_id || ""); }}>
                {p.assigned_worker_id ? "Change Worker" : "Assign Worker"}
              </Button>
            </div>
          ))}
          {parents.length === 0 && <p className="py-6 text-center text-sm text-slate-400">No parents registered yet.</p>}
        </div>
      </div>

      {/* Assign dialog */}
      <Dialog open={!!assignFor} onOpenChange={(o) => !o && setAssignFor(null)}>
        <DialogContent className="sm:max-w-md" data-testid="assign-worker-modal">
          <DialogHeader>
            <DialogTitle className="font-display">Assign Client to Worker</DialogTitle>
            <DialogDescription>{assignFor && `${assignFor.name}${assignFor.county_name ? ` · ${assignFor.county_name}` : ""}`}</DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label>Active social worker</Label>
            <select
              data-testid="assign-worker-select"
              value={assignWorkerId}
              onChange={(e) => setAssignWorkerId(e.target.value)}
              className="flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm"
            >
              <option value="">Unassigned</option>
              {activeWorkers.map((w) => (
                <option key={w.id} value={w.id}>{w.name}{w.county_name ? ` (${w.county_name})` : ""}</option>
              ))}
            </select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignFor(null)}>Cancel</Button>
            <Button data-testid="confirm-assign-worker-btn" onClick={assignParent} className="bg-[#1e3a8a] hover:bg-[#172554]">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
